import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'

/** A router query object, or anything shaped like one. */
type RouterQuery = Record<string, unknown>

function normalizeRouterQuery(value: RouterQuery) {
  return Object.fromEntries(
    Object.entries(value)
      // Remove undefined or empty arrays
      .filter(([_key, value]) => !(value === undefined || (Array.isArray(value) && value.length === 0)))
      // Ensure every value is made into an array
      .map(([key, value]) => [key, Array.isArray(value) ? value.map((value) => String(value)) : [String(value)]]),
  )
}

/**
 * Returns true when the two inputs are equal, taking into account that the router sometimes stores values in an array
 *
 * For example:
 * { foo: "Foo", bar: 42, unspecified: undefined, empty: [] } is equal to { foo: ["Foo"], bar: ["42"] }
 */
export function routerQueryIsEqual(a: RouterQuery, b: RouterQuery) {
  return isEqual(normalizeRouterQuery(a), normalizeRouterQuery(b))
}

/**
 * Compares current with update and returns a structure usable as a router `query` parameter
 *
 * @todo consider moving this code into a library for better re-usability
 */
export function checkDynamicUrl(current: RouterQuery, update: RouterQuery) {
  // Normalize `current` and `update` into a `{foo: [value, value, ...]}` structure
  const normalizedUpdate = Object.fromEntries(Object.entries(update).map(([key, value]) => [key, value === undefined ? [] : Array.isArray(value) ? value : [value]]))
  const normalizedCurrent = Object.fromEntries([
    ...Object.keys(normalizedUpdate).map((key) => [key, []]),
    ...Object.entries(current).map(([key, value]) => [key, value === undefined ? [] : Array.isArray(value) ? value : [value]]),
  ])

  return {
    // Compare only the keys specified in `update`
    isEqual: isEqual(pick(normalizedCurrent, Object.keys(normalizedUpdate)), normalizedUpdate),
    mergedQuery: { ...normalizedCurrent, ...normalizedUpdate },
  }
}

/**
 * Watches `input` and updates the URL with the result from `queryCallback` when changes occur
 *
 * Note that `input` is most likely a `ref(...)` and the value passed to the callback is `unref(input)`
 * for convenience.
 *
 * For example:
 * ```js
 * setupDynamicUrl(input, (value) => ({
 *   search: value.search,
 *   page: value.page === 1 ? undefined : value.page,
 * }))
 * ```
 *
 * @todo consider moving this code into a library for better re-usability
 */
export function setupDynamicUrl<T>(input: Ref<T>, queryCallback: (_value: T) => RouterQuery) {
  const route = useRoute()
  const router = useRouter()
  watch(
    input,
    () => {
      const { isEqual, mergedQuery } = checkDynamicUrl(unref(route.query), queryCallback(unref(input)))
      if (!isEqual) {
        router.push({ ...unref(route), query: mergedQuery })
      }
    },
    { deep: true },
  )
}
